<template>
  <transition>
    <div class="mod-dial lm-dialog lm-category" @click="requestClose" @keydown.esc="onEscape">
      <div class="lm-shell" role="dialog" aria-modal="true" aria-labelledby="create-category-title" tabindex="-1" ref="dialog" @click.stop>
        <header class="lm-header">
          <div>
            <h1 id="create-category-title">{{ $t('CREATE_CATEGORY_TITLE') }}</h1>
            <p>{{ $t('LM_CATEGORY_DESC') }}</p>
          </div>
          <span>
            <button type="button" class="lm-close" :aria-label="$t('ACCESS_MANAGER_CLOSE')" @click="requestClose">
              <i class="bi bi-x-lg"></i>
            </button>
            <div v-if="confirmDiscard" class="delete-confirm" @click.stop>
              <p>{{ $t('LM_UNSAVED_BODY') }}</p>
              <div class="delete-actions">
                <button type="button" class="btn-cancel" @click="confirmDiscard = false">{{ $t('LM_KEEP_EDITING') }}</button>
                <button type="button" class="btn-confirm" @click="closeCreate">{{ $t('LM_DISCARD') }}</button>
              </div>
            </div>
          </span>
        </header>
        <div class="lm-scroll">
          <form @submit.prevent="createCategory" class="lm-invite">
            <label class="lm-field email-field">
              <input
                type="text"
                class="inp-st"
                :placeholder="$t('CREATE_CATEGORY_PLACEHOLDER')"
                v-model="categoryName"
                maxlength="25"
              />
            </label>
            <button class="btn-st-red" type="submit" :disabled="isCategoryLimitReached">
              {{ $t('CREATE_CATEGORY_CREATE') }}
            </button>
          </form>
          <p v-if="getProfile && getProfile.pageLimit && isCategoryLimitReached" class="lm-error">
            {{ $t('CREATE_CATEGORY_LIMIT_REACHED', { limit: getProfile.pageLimit }) }}
          </p>
          <p v-else-if="getProfile && getProfile.pageLimit && remainingCategories <= 2" class="lm-empty">
            {{ $t('CREATE_CATEGORY_LIMIT_APPROACHING', { remaining: remainingCategories }) }}
          </p>

          <template v-if="getSelectedPages.length">
            <div class="lm-people-head">
              <span>{{ $t('CREATE_CATEGORY_SELECTED', { count: getSelectedPages.length }) }}</span>
            </div>
            <table class="lm-table">
              <thead>
                <tr>
                  <th>{{ $t('INTRO_TABS_NAME') }}</th>
                  <th>{{ $t('INTRO_TABS_SOCIAL') }}</th>
                  <th>{{ $t('ACCESS_MANAGER_ACTION') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(page, index) in getSelectedPages" :key="index">
                  <td class="lm-page-cell">
                    <img :src="page.owner_avatar" alt="" class="lm-page-logo" />
                    {{ truncateName(page.owner_name) }}
                  </td>
                  <td>
                    <span :class="`soc-ico soc-${String(page.social || '').toLowerCase()}`"></span>
                  </td>
                  <td>
                    <button type="button" class="lm-remove" @click="removeFromSelection(page)">
                      {{ $t('INTRO_TABS_DELETE') }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>

          <template v-if="categoriesWithHover.length">
            <div class="lm-people-head">
              <span>{{ $t('CREATE_CATEGORY_EXISTING') }}</span>
            </div>
            <table class="lm-table">
              <thead>
                <tr>
                  <th>{{ $t('CATEGORY_ACCESS_CATEGORY') }}</th>
                  <th>{{ $t('ACCESS_MANAGER_ACTION') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="category in categoriesWithHover" :key="category.id">
                  <td>
                    <span :class="`cat-indicator ${getCategoryColorClass(category.id)}`"></span>
                    {{ category.name }} ({{ getCategoryPageCount(category) }})
                  </td>
                  <td>
                    <button type="button" class="lm-remove" @click="addToExistingCategory(category)">
                      {{ $t('CREATE_CATEGORY_ADD') }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
          <div ref="alertContainer"></div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import {
  CATEGORIES_CREATE,
  CATEGORIES_UPDATE,
  CATEGORIES_SET_CREATE_VISIBLE,
  CATEGORIES_ADD_PAGE,
} from "../../store/actions/categories";
import {
  INTRO_SET_SELECTED,
} from "../../store/actions/intro";

export default {
  name: "CreateCategory",
  computed: {
    ...mapGetters(["getSelectedPages", "getCategories", "getProfile"]),
    
    getCategoryPageCount() {
      return (category) => {
        if (category.pages) {
          return category.pages.length;
        } else if (category.page_ids) {
          return category.page_ids.length;
        }
        return 0;
      };
    },
    
    categoriesWithHover() {
      return this.getCategories.map(category => ({
        ...category
      }));
    },
    
    isCategoryLimitReached() {
      if (!this.getProfile || !this.getProfile.pageLimit) {
        return false;
      }
      return this.getCategories.length >= this.getProfile.pageLimit;
    },
    
    remainingCategories() {
      if (!this.getProfile || !this.getProfile.pageLimit) {
        return 0;
      }
      return Math.max(0, this.getProfile.pageLimit - this.getCategories.length);
    },
  },
  data() {
    return {
      categoryName: "",
      confirmDiscard: false,
    };
  },
  watch: {
    getSelectedPages: {
      handler(newPages) {
        // Close modal if all pages are removed
        if (newPages.length === 0) {
          this[CATEGORIES_SET_CREATE_VISIBLE](false);
        }
      },
      immediate: false,
    },
  },
  methods: {
    ...mapActions([CATEGORIES_CREATE, CATEGORIES_UPDATE, CATEGORIES_ADD_PAGE, INTRO_SET_SELECTED]),
    ...mapMutations([CATEGORIES_SET_CREATE_VISIBLE]),

    async createCategory() {
      if (!this.categoryName.trim()) {
        this.showInlineAlert(this.$t('CREATE_CATEGORY_NAME_REQUIRED'));
        return;
      }

      if (!this.getSelectedPages.length) {
        this.showInlineAlert(this.$t('CREATE_CATEGORY_PAGES_REQUIRED'));
        return;
      }

      // Check if category limit is reached
      if (this.isCategoryLimitReached) {
        this.showInlineAlert(this.$t('CREATE_CATEGORY_LIMIT_REACHED', { 
          limit: this.getProfile.pageLimit 
        }));
        return;
      }

      const pageIds = this.getSelectedPages.map(page => page.page_id);

      try {
        const categoryName = this.categoryName.trim();
        await this[CATEGORIES_CREATE]({
          name: categoryName,
          page_ids: pageIds
        });
        
        this.categoryName = "";
        this[CATEGORIES_SET_CREATE_VISIBLE](false);
        
        this.$store.dispatch('alerts/addAlert', {
          message: `Category "${categoryName}" created successfully`,
          color: 'success',
          duration: 5000
        });
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t('CREATE_CATEGORY_ERROR'));
        }
      }
    },

    async addToExistingCategory(category) {
      if (!this.getSelectedPages.length) {
        this.showInlineAlert(this.$t('CREATE_CATEGORY_PAGES_REQUIRED'));
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const pagesToAdd = this.getSelectedPages;

      try {
        // Use individual page operations for better error handling and granular control
        const addPromises = pagesToAdd.map(async (page) => {
          try {
            await this[CATEGORIES_ADD_PAGE]({
              page_id: page.page_id,
              category_id: category.id
            });
            successCount++;
          } catch (error) {
            errorCount++;
            console.error(`Failed to add page ${page.owner_name} to category:`, error);
            // Handle specific error cases if needed
            if (error?.response?.data?.error_code) {
              // Could show individual page errors if needed
            }
          }
        });

        await Promise.all(addPromises);

        // Show appropriate success/error message
        if (successCount > 0 && errorCount === 0) {
          this.showInlineAlert(this.$t('CREATE_CATEGORY_ADDED_SUCCESS', { categoryName: category.name }));
        } else if (successCount > 0 && errorCount > 0) {
          this.showInlineAlert(this.$t('CREATE_CATEGORY_PARTIAL_SUCCESS', { 
            categoryName: category.name, 
            success: successCount, 
            total: pagesToAdd.length 
          }));
        } else {
          this.showInlineAlert(this.$t('CREATE_CATEGORY_ADD_ERROR'));
        }

        // Close the modal after a short delay if all or most pages were added
        if (successCount > 0) {
          setTimeout(() => {
            this[CATEGORIES_SET_CREATE_VISIBLE](false);
          }, 1500);
        }
        
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t('CREATE_CATEGORY_ADD_ERROR'));
        }
      }
    },

    truncateName(name) {
      if (!name) return '';
      const maxLength = 25;
      return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
    },

    getCategoryColorClass(categoryId) {
      if (!categoryId) return 'gray-back';
      
      // Get color from localStorage synchronously 
      const stored = localStorage.getItem('categoryColors');
      const colors = stored ? JSON.parse(stored) : {};
      const color = colors[categoryId] || 'gray';
      
      return color + '-back';
    },

    removeFromSelection(page) {
      this[INTRO_SET_SELECTED](page);
    },
    requestClose() {
      if (this.confirmDiscard) return;
      if ((this.categoryName || "").trim()) {
        this.confirmDiscard = true;
        return;
      }
      this.closeCreate();
    },
    onEscape() {
      if (this.confirmDiscard) return;
      this.requestClose();
    },
    closeCreate() {
      this.confirmDiscard = false;
      this[CATEGORIES_SET_CREATE_VISIBLE](false);
    },

    showInlineAlert(message) {
      this.$alert.show({
        message: message,
        type: 'inline',
        color: '',
        component: this,
        target: this.$refs.alertContainer,
      });
    },
  },
};
</script>

<style>
</style> 